import React from 'react';
import { FiLoader } from 'react-icons/fi'
import styled, { css, keyframes } from 'styled-components'
import { pxToRem } from 'styles/functions'

const getFlex = (columns) => {
  switch (columns) {
    case '3':
      return '33%'
    case '4':
      return '25%'
    case '7':
      return '14%'
    default:
      return '43%'
  }
}

const getFontSize = (columns) => {
  switch (columns) {
    case '3':
      return pxToRem(13)
    case '4':
      return pxToRem(12)
    default:
      return pxToRem(15)
  }
}

interface StyledImageButtonProps {
  theme: any
  $borderWidth: string
  $borderRadius: string
  $nbrPerCol: string
}

export const StyledImageButton = styled.button<StyledImageButtonProps>`
  background-size: cover;
  background-position: center center;
  margin: 0 auto ${pxToRem(10)} auto;
  width: ${({ $nbrPerCol }) => getFlex($nbrPerCol)};
  
  border-radius: ${({ $borderRadius }) => $borderRadius}%;
  border: ${({ $borderWidth }) => $borderWidth}px solid black;

  &::after {
    content: '';
    display: block;
    padding-bottom: 100%;
  }
`

const spin = keyframes`
  from {
    transform:rotate(0deg);
  }
  to {
    transform:rotate(360deg);
  }
`

interface StyleButtonFlatProps {
  theme: any;
}
const styleButtonFlat = css<StyleButtonFlatProps>`
  width: 66%;
  margin: auto;
  padding: 2px 6px;
  font-size: 12px;
  line-height: 15px;
    
  border-radius: ${({ theme }) => pxToRem(theme.button_radius / 1.5 || 0)};
`;

interface StyledLoaderProps {
  theme: any;
}
export const StyledLoader = styled(FiLoader) <StyledLoaderProps>`
  ${({ theme }) => theme.trigger_hide_loader && 'display: none'};
  margin-left: ${pxToRem(10)};
  animation: ${spin} 3s linear infinite;
`

const getButtonColor = (theme, isPhotobooth) => {
  const color = theme.button_color || '#34b076'
  if (isPhotobooth) {
    return theme.photobooth_button_color || color
  }
  return color
}

const getTextColor = (theme, isPhotobooth) => {
  if (isPhotobooth) {
    return theme.photobooth_button_text_color || theme.button_text_color || 'white'
  }
  return theme.button_text_color || 'white'
}

interface StyledButtonContainerProps {
  theme: any;
  $columns: string;
}
export const StyledButtonContainer = styled.div<StyledButtonContainerProps>`
  display: flex;
  padding: ${pxToRem(20)};
  flex-wrap: wrap;
  align-items: ${({ theme }) => (theme.button_centered ? 'center' : 'flex-start')};
  margin: ${pxToRem(20)};
  flex-flow: column;

  span {
    align-self: center;
    ${({ theme }) => theme.button_half_font && `font-family: ${theme.button_half_font}`};
    font-weight: ${({ theme }) => theme.button_half_text_weight || '600'};
    font-size: ${({ $columns, theme }) =>
      theme.button_half_resize_label ? getFontSize($columns) : pxToRem(theme.button_half_text_size || 15)};
    word-break: break-word;
    ${({ $columns }) =>
      $columns === '7' &&
      `font-size: ${pxToRem(12)};
      display: block;
      margin-left: -${pxToRem(9)};
      margin-right: -${pxToRem(9)};
      padding-left: 1px; 
      padding-right: 1px; 
      `}
    ${({ theme }) => theme.button_half_text_color && `color: ${theme.button_half_text_color}`};
  }
`

interface StyledTextBeforeProps {
  theme: any;
}
export const StyledTextBefore = styled.div<StyledTextBeforeProps>`
    width: 100%;
    font-size: ${({ theme }) => (theme.text_size ? pxToRem(theme.text_size) : '14px')};
    text-align: ${({ theme }) => (theme.button_text_before_after_align ? theme.button_text_before_after_align : 'center')};
    margin-bottom: 3px;
`

interface StyledTextAfterProps {
  theme: any;
}
export const StyledTextAfter = styled.div<StyledTextAfterProps>`
    width: 100%;
    font-size: ${({ theme }) => (theme.text_size ? pxToRem(theme.text_size) : '14px')};
    text-align: ${({ theme }) => (theme.button_text_before_after_align ? theme.button_text_before_after_align : 'center')};
    margin-top: 3px;
`

interface StyledButtonProps {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
}



export const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-all;

  ${({ theme }) => theme.button_background_url && css`background: url(${theme.button_background_url});`};

  background-color: ${({ theme, $isPhotobooth }) => getButtonColor(theme, $isPhotobooth)};
  color: ${({ theme, $isPhotobooth }) => getTextColor(theme, $isPhotobooth)};
  padding: ${({ theme }) => (theme.button_padding ? theme.button_padding : pxToRem(12))};
  width: ${({ theme }) => (theme.button_width ? pxToRem(theme.button_width) : '100%')};

  ${({ theme, $isPhotobooth }) => !theme.button_no_border && css`border: ${pxToRem(theme.button_border_width || 2)} solid ${theme.button_border_color || getButtonColor(theme, $isPhotobooth)};`}

  border-radius: ${({ theme }) => pxToRem(theme.button_radius || 0)};
  box-shadow: ${({ theme }) => theme.button_shadow || 'none'};
  transition: all 0.2s;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 0.1);
  cursor: pointer;

  &.disabled {
    color: white;
    pointer-events: none;
    background-color: lightgrey;
    border-color: lightgrey;
    cursor: not-allowed;
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    pointer-events: none;
    ${({ theme }) => theme.button_text_size && css`font-size: ${pxToRem(theme.button_text_size)};`};
    ${({ theme }) => theme.button_font && css`font-family: ${theme.button_font};`};
    ${({ theme }) => theme.button_text_uppercase && css`text-transform: uppercase;`};
    ${({ theme }) => theme.button_text_weight && css`font-weight: ${theme.button_text_weight};`};
    ${({ theme }) => theme.button_text_lineheight && css`line-height: ${pxToRem(theme.button_text_lineheight)};`};
    ${({ theme }) => theme.button_text_letter_spacing && css`letter-spacing: ${pxToRem(theme.button_text_letter_spacing)};`};
  }

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme }) => (theme.button_hover_color ? theme.button_hover_color : 'transparent')};
    color: ${({ theme, $isPhotobooth }) => theme.button_hover_text_color || getButtonColor(theme, $isPhotobooth)};
    ${({ theme }) => (theme.button_hover_color || theme.button_hover_border_color) && css`border-color: ${({ theme }) => (theme.button_hover_border_color ? theme.button_hover_border_color : theme.button_border_color)};`}
  
    
  }

  ${({ $flat }) => $flat && css`${styleButtonFlat}`};

  ${({ theme }) => theme.custom_button && theme.custom_button}
`

interface StyledButtonOutlinedProps extends React.SVGProps<SVGSVGElement> {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
}

export const StyledButtonIcon = styled.svg`
  width: 20px;
  height: 20px;
  margin-right: 5px;
  fill: currentColor;
  transition: fill 0.2s;
`;

export const StyledButtonOutlined = styled(StyledButton) <StyledButtonOutlinedProps>`
  background-color: ${({ theme }) => theme.button_outlined_color || 'transparent'};
  color: ${({ theme, $isPhotobooth }) => theme.button_outlined_text_color || getButtonColor(theme, $isPhotobooth)};
  border-color: ${({ theme }) => theme.button_outlined_border_color || theme.button_outlined_text_color};
  word-break: break-all;

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme, $isPhotobooth }) => theme.button_outlined_hover_color || getButtonColor(theme, $isPhotobooth)};
    color: ${({ theme }) => theme.button_outlined_hover_text_color || 'white'};
    border-color: ${({ theme, $isPhotobooth }) => theme.button_outlined_hover_border_color ? theme.button_outlined_hover_border_color : theme.button_outlined_hover_color || getButtonColor(theme, $isPhotobooth)};
    }

  ${({ $flat }) => $flat && css`${styleButtonFlat}`};

  ${({ theme }) => theme.custom_alternate_button && theme.custom_alternate_button}
`